<script>
/* global axios */
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import router from 'dashboard/routes/index';
import PageHeader from 'dashboard/routes/dashboard/settings/SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'EvolutionChannel',
  components: { PageHeader, NextButton },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      channelName: '',
      selectedProviderId: null,
      providers: [],
      isLoadingProviders: true,
      isCreating: false,
    };
  },
  computed: {
    ...mapGetters({ currentAccountId: 'getCurrentAccountId' }),
    providerOptions() {
      return this.providers
        .filter(p => p.provider_type === 'evolution')
        .map(p => ({ value: p.id, label: p.name }));
    },
  },
  validations: {
    channelName: { required },
    selectedProviderId: { required },
  },
  async mounted() {
    try {
      const resp = await axios.get(
        `/api/v1/accounts/${this.currentAccountId}/kanban/connections/providers`
      );
      this.providers = resp.data.data || [];
      if (this.providerOptions.length === 1) {
        this.selectedProviderId = this.providerOptions[0].value;
      }
    } catch {
      useAlert('Erro ao carregar provedores Evolution.');
    } finally {
      this.isLoadingProviders = false;
    }
  },
  methods: {
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) return;
      this.isCreating = true;
      try {
        // 1. Cria inbox Channel::Api
        const inbox = await this.$store.dispatch('inboxes/createChannel', {
          name: this.channelName.trim(),
          channel: { type: 'api', webhook_url: '' },
        });

        // 2. Vincula ao provedor Evolution (cria instancia + configura webhook)
        await axios.post(
          `/api/v1/accounts/${this.currentAccountId}/kanban/connections/link_inbox`,
          { provider_id: this.selectedProviderId, inbox_id: inbox.id }
        );

        // 3. Navega para adicionar agentes
        router.replace({
          name: 'settings_inboxes_add_agents',
          params: { page: 'new', inbox_id: inbox.id },
        });
      } catch (err) {
        useAlert(
          err?.response?.data?.error || 'Erro ao criar conexao Evolution.'
        );
      } finally {
        this.isCreating = false;
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      header-title="WhatsApp (Evolution)"
      header-content="Conecte um numero de WhatsApp via Evolution API."
    />

    <div v-if="isLoadingProviders" class="py-4 text-sm text-gray-500">
      Carregando provedores...
    </div>

    <div v-else-if="providerOptions.length === 0" class="py-4">
      <p class="text-sm text-red-600">
        Nenhum provedor Evolution disponivel. Solicite ao administrador.
      </p>
    </div>

    <form v-else class="flex flex-col gap-4 mt-4" @submit.prevent="createChannel">
      <label :class="{ error: v$.channelName.$error }">
        Nome da caixa de entrada
        <input
          v-model="channelName"
          type="text"
          placeholder="Ex: Vendas WhatsApp"
          @blur="v$.channelName.$touch"
        />
        <span v-if="v$.channelName.$error" class="message">
          Nome obrigatorio
        </span>
      </label>

      <label v-if="providerOptions.length > 1" :class="{ error: v$.selectedProviderId.$error }">
        Provedor Evolution
        <select v-model="selectedProviderId" @blur="v$.selectedProviderId.$touch">
          <option :value="null" disabled>Selecione o provedor</option>
          <option v-for="opt in providerOptions" :key="opt.value" :value="opt.value">
            {{ opt.label }}
          </option>
        </select>
        <span v-if="v$.selectedProviderId.$error" class="message">
          Selecione um provedor
        </span>
      </label>

      <div class="mt-2">
        <NextButton
          :is-loading="isCreating"
          :disabled="providerOptions.length === 0"
          type="submit"
          solid
          blue
          label="Criar conexao"
        />
      </div>
    </form>
  </div>
</template>
